"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/app/providers/AxiosProvider";
import useRecruiterDetails from "@/lib/user/useRecruiterDetails";
import { findingsToData } from "./mapFindings";
import { normaliseDomain } from "./normaliseDomain";

/**
 * Onboarding state machine.
 *
 * Client status:
 *   idle → needs_email? → researching → ready → completed | error
 *
 * On first login (server `onboarding_status == null`) it auto-runs the research
 * stream, persists the findings for review (`ready_for_review`), and surfaces a
 * CTA. "Looks great" applies the (edited) fields (`completed`); "Review later"
 * just closes the modal, leaving the CTA so the nudge persists across logins.
 *
 * Research is held back while the recruiter still owes us a business email
 * (`needs_business_email`) — a social registration on a personal address has no
 * company domain to research, so we nudge for one instead of researching
 * gmail.com.
 */
export const ONBOARDING_STATUS = {
  IDLE: "idle",
  NEEDS_EMAIL: "needs_email",
  RESEARCHING: "researching",
  READY: "ready",
  COMPLETED: "completed",
  ERROR: "error",
};

const OnboardingContext = createContext(null);

/**
 * Read the SSE stream from the onboarding research route, calling `onUpdate`
 * for each phase. Resolves with the final findings (or throws on an error phase).
 */
async function readResearchStream(response, onUpdate) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let findings = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let boundary;
    while ((boundary = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, boundary).trim();
      buffer = buffer.slice(boundary + 2);

      if (!raw.startsWith("data:")) continue;

      let update;
      try {
        update = JSON.parse(raw.slice(5).trim());
      } catch {
        continue;
      }

      onUpdate(update);

      if (update.phase === "error") {
        throw new Error(update.error || update.message || "Research failed");
      }
      if (update.phase === "complete" && update.findings) {
        findings = update.findings;
      }
    }
  }

  return findings;
}

export function OnboardingProvider({ children }) {
  const api = useAxios();
  const queryClient = useQueryClient();
  const { recruiter } = useRecruiterDetails();
  const recruiterId = recruiter?.uuid;
  const businessEmail = recruiter?.business_email || "";
  const needsBusinessEmail = Boolean(recruiter?.needs_business_email);

  // The domain a re-run would research, for the confirmation prompt to prefill.
  // Same precedence the server applies, plus the recruiter's own website — by
  // the time they can ask for a re-run, that is often the better signal.
  const researchDomain =
    normaliseDomain(businessEmail) ||
    normaliseDomain(recruiter?.website) ||
    normaliseDomain(recruiter?.user?.email) ||
    "";

  // Status is derived from the persisted server status, with locally-driven
  // transitions (researching, applied, failed) taking precedence once they
  // happen — the server catches up on the next /me refetch.
  const [localStatus, setStatus] = useState(null);
  const serverStatus = recruiter?.onboarding_status;
  const derivedStatus =
    serverStatus == null
      ? needsBusinessEmail
        ? ONBOARDING_STATUS.NEEDS_EMAIL
        : ONBOARDING_STATUS.IDLE
      : serverStatus === "ready_for_review"
        ? ONBOARDING_STATUS.READY
        : serverStatus === "completed"
          ? ONBOARDING_STATUS.COMPLETED
          : ONBOARDING_STATUS.IDLE;
  const status = localStatus ?? derivedStatus;
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [domainPromptOpen, setDomainPromptOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const runningRef = useRef(false);

  /**
   * Run the research stream. `domain` overrides the domain we would otherwise
   * derive — a re-run from Settings has the recruiter confirm it first, since
   * the stored work email can point at the wrong business.
   */
  const start = useCallback(
    async ({ domain } = {}) => {
      if (runningRef.current || !recruiterId) return;

      runningRef.current = true;
      setError(null);
      setProgress(0);
      setMessage("Preparing onboarding research...");
      setStatus(ONBOARDING_STATUS.RESEARCHING);

      try {
        // Prefer the captured work email's domain — the session email can be the
        // personal address a LinkedIn registration arrived on.
        const response = await fetch("/api/onboarding/research", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...(businessEmail ? { email: businessEmail } : {}),
            ...(domain ? { domain } : {}),
          }),
        });
        if (!response.ok || !response.body) {
          throw new Error("Could not start onboarding research");
        }

        const findings = await readResearchStream(response, (update) => {
          if (typeof update.progress === "number") setProgress(update.progress);
          if (update.message) setMessage(update.message);
        });

        if (!findings) {
          throw new Error("Onboarding research returned no findings");
        }

        const mapped = findingsToData(findings);

        // Surface the review CTA immediately from the in-memory findings, then
        // persist in the background — a save hiccup must not strand the user
        // without any way to review what was gathered.
        setData(mapped);
        setMessage("");
        setStatus(ONBOARDING_STATUS.READY);

        try {
          await api.post(`/recruiter/${recruiterId}/onboarding`, mapped);
          queryClient.invalidateQueries({ queryKey: ["recruiter-details"] });
        } catch (persistError) {
          console.error(
            "[Onboarding] Failed to persist findings for review",
            persistError,
          );
        }
      } catch (e) {
        setError(e.message);
        setStatus(ONBOARDING_STATUS.ERROR);
        setMessage("Onboarding research failed.");
      } finally {
        runningRef.current = false;
      }
    },
    [recruiterId, businessEmail, api, queryClient],
  );

  // Kick off the research on first login — once we have a recruiter, they have
  // never been onboarded, and they don't still owe us a work email.
  useEffect(() => {
    if (!recruiter || serverStatus != null) return;
    if (needsBusinessEmail || localStatus || runningRef.current) return;

    start();
  }, [recruiter, serverStatus, needsBusinessEmail, localStatus, start]);

  // Social signups used to be diverted to a dedicated page before they ever
  // reached the dashboard. They now land here, so ask once on arrival — if they
  // skip, the status bar keeps the nudge and reopens this on demand.
  const emailPromptShownRef = useRef(false);
  useEffect(() => {
    if (!needsBusinessEmail || emailPromptShownRef.current) return;

    emailPromptShownRef.current = true;
    setEmailPromptOpen(true);
  }, [needsBusinessEmail]);

  const openEmailPrompt = useCallback(() => setEmailPromptOpen(true), []);
  const closeEmailPrompt = useCallback(() => setEmailPromptOpen(false), []);

  /**
   * Store the recruiter's work email alongside their login email. Once the
   * refetched recruiter no longer needs one, the effect above releases the
   * research run — so this deliberately awaits the invalidation rather than
   * starting the research itself.
   *
   * Throws on failure so the dialog can surface the API's validation message.
   */
  const saveBusinessEmail = useCallback(
    async (email) => {
      if (!recruiterId) return;

      await api.post(`/recruiter/${recruiterId}/business-email`, {
        business_email: email,
      });
      await queryClient.invalidateQueries({ queryKey: ["recruiter-details"] });
      setEmailPromptOpen(false);
    },
    [recruiterId, api, queryClient],
  );

  const openModal = useCallback(async () => {
    if (!data && recruiterId) {
      try {
        const res = await api.get(`/recruiter/${recruiterId}/onboarding`);
        setData(res.data?.onboarding_data ?? null);
      } catch {
        // fall through — the modal opens with whatever we have
      }
    }
    setModalOpen(true);
  }, [data, recruiterId, api]);

  const reviewLater = useCallback(() => {
    setModalOpen(false);
  }, []);

  const apply = useCallback(
    async (edited) => {
      if (!recruiterId) return;

      setApplying(true);
      try {
        await api.post(`/recruiter/${recruiterId}/onboarding/apply`, edited);
        setData(edited);
        setStatus(ONBOARDING_STATUS.COMPLETED);
        setModalOpen(false);
        queryClient.invalidateQueries({ queryKey: ["recruiter-details"] });
      } finally {
        setApplying(false);
      }
    },
    [recruiterId, api, queryClient],
  );

  const runAgain = useCallback(
    (domain) => {
      setModalOpen(false);
      setDomainPromptOpen(false);
      setData(null);
      // Guarded because the obvious wiring for a re-run button is
      // `onClick={runAgain}`, which would hand us a click event to research.
      start({ domain: typeof domain === "string" ? domain : undefined });
    },
    [start],
  );

  /**
   * Ask which domain to research before re-running.
   *
   * Mounted at the layout level like the other onboarding dialogs rather than
   * nested inside whatever opened it — the caller's own modal closes as this
   * opens (the run reports progress in the status bar behind it), which would
   * take a nested dialog down with it.
   */
  const openDomainPrompt = useCallback(() => {
    setModalOpen(false);
    setDomainPromptOpen(true);
  }, []);
  const closeDomainPrompt = useCallback(() => setDomainPromptOpen(false), []);

  const value = {
    status,
    progress,
    message,
    data,
    error,
    modalOpen,
    emailPromptOpen,
    domainPromptOpen,
    applying,
    needsBusinessEmail,
    researchDomain,
    barVisible:
      status === ONBOARDING_STATUS.NEEDS_EMAIL ||
      status === ONBOARDING_STATUS.RESEARCHING ||
      status === ONBOARDING_STATUS.READY ||
      status === ONBOARDING_STATUS.ERROR,
    start,
    openModal,
    openEmailPrompt,
    closeEmailPrompt,
    openDomainPrompt,
    closeDomainPrompt,
    saveBusinessEmail,
    reviewLater,
    apply,
    runAgain,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
}
